import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import io from 'socket.io-client';
import websocketService from '../services/websocket';
import { API_ORIGIN, AI_SERVICE_BASE_URL } from '../services/api';
import { apiService } from '../services/apiWrapper';
import { useRealtimeMonitoring, useRealtimeMonitoringData } from '../context/RealtimeMonitoringContext';
import './SmartMonitoring.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ======== ТУК СА ФЛАГОВЕТЕ ЗА ИЗКЛЮЧВАНЕ ========
// eslint-disable-next-line no-unused-vars -- reserved for future use
const DISABLE_BACKEND_WS = true; // изключва websocketService (realtime_data/alert/device_status)
const DISABLE_AI_WS = false;     // изключва AI Socket.IO връзката
// ===============================================

const SmartMonitoring = () => {
    console.log('🟡 [DEBUG] SmartMonitoring компонентът се рендерира!');
    const [activeTab, setActiveTab] = useState('realtime'); // 'realtime' or 'ai'
    const { startMonitoring: startRealtimeFromContext, stopMonitoring: stopRealtimeFromContext, updateRealtimeData } = useRealtimeMonitoring();
    const { monitoringActive, currentData, tempData, smokeData, timeLabels } = useRealtimeMonitoringData();
    const chartMountIdRef = useRef(Date.now());

    // При отваряне/връщане на realtime таб – resize за Chart.js след кратко закъснение (важно след връщане от друга страница)
    useEffect(() => {
        if (activeTab !== 'realtime') return;
        const t1 = setTimeout(() => {
            requestAnimationFrame(() => {
                if (typeof window !== 'undefined') window.dispatchEvent(new Event('resize'));
            });
        }, 150);
        const t2 = monitoringActive ? setTimeout(() => {
            requestAnimationFrame(() => {
                if (typeof window !== 'undefined') window.dispatchEvent(new Event('resize'));
            });
        }, 450) : null;
        return () => {
            clearTimeout(t1);
            if (t2) clearTimeout(t2);
        };
    }, [activeTab, monitoringActive]);

    // Debug: Логване на промяна на таба и зареждане на данни
    useEffect(() => {
        console.log('📌 Активен таб променен на:', activeTab);
        if (activeTab === 'realtime') {
            console.log('🔄 Зареждане на данни за реално време...');
            loadMonitoringData();
            loadDeviceHealth();
        }
    }, [activeTab]);

    // Device health state
    const [deviceHealth, setDeviceHealth] = useState([]);
    const [systemHealth, setSystemHealth] = useState({
        overall: 'good',
        deviceCount: { total: 0, online: 0, offline: 0, alarm: 0 },
        lastCheck: null
    });

    // Fire safety equipment monitoring state
    const [monitoringData, setMonitoringData] = useState({
        sprinklerZones: [],
        electricalPanels: [],
        evacDoors: [],
        smokeDetectors: [],
        heatSensors: [],
        cameras: [],
        alarms: [],
        doorSensors: []
    });
    const [monitoringLoading, setMonitoringLoading] = useState(false);
    const backendSocketRef = useRef(null);
    
    // AI state
    const [aiStatus, setAiStatus] = useState({
        connected: false,
        provider: 'none',
        monitoring: false,
        hasProvider: false // дали има конфигуриран AI провайдер
    });
    const [aiInsights, setAiInsights] = useState([]);
    const [emergencyAlerts, setEmergencyAlerts] = useState([]);
    const [predictions, setPredictions] = useState(null);
    
    const aiSocketRef = useRef(null);
    const aiStatusCheckRef = useRef(null); // За предотвратяване на множествени проверки
    const aiConnectionAttemptsRef = useRef(0);
    const MAX_AI_CONNECTION_ATTEMPTS = 3; // Максимален брой опити за свързване

    const loadDeviceHealth = async () => {
        try {
            const response = await apiService.devices.getAll();
            const data = response?.data;
            const healthData = Array.isArray(data) ? data : (data?.devices || data?.data || []);
            if (healthData.length >= 0) {
                const healthSummary = {
                    total: healthData.length,
                    online: healthData.filter(d => d.status === 'online').length,
                    offline: healthData.filter(d => d.status === 'offline').length,
                    alarm: healthData.filter(d => d.status === 'alarm').length
                };

                // Определи общото здраве на системата
                let overallHealth = 'good';
                if (healthSummary.alarm > 0) {
                    overallHealth = 'critical';
                } else if (healthSummary.offline > healthSummary.total * 0.3) {
                    overallHealth = 'warning';
                } else if (healthSummary.offline > 0) {
                    overallHealth = 'moderate';
                }

                setDeviceHealth(healthData);
                setSystemHealth({
                    overall: overallHealth,
                    deviceCount: healthSummary,
                    lastCheck: new Date().toISOString()
                });
            }
        } catch (error) {
            console.error('Error loading device health data:', error);
        }
    };

    useEffect(() => {
        setupConnections();
        loadDeviceHealth();
        loadMonitoringData();
        checkAIStatusViaHTTP();
        return () => {
            cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMonitoringData = async () => {
        setMonitoringLoading(true);
        console.log('🔄 [SmartMonitoring] Зареждане на мониторинг данни...');
        
        try {
            // Проверка на token преди заявките
            const token = localStorage.getItem('fireguard_token');
            console.log('🔑 [SmartMonitoring] Token проверка:', {
                hasToken: !!token,
                tokenLength: token ? token.length : 0,
                tokenPreview: token ? token.substring(0, 20) + '...' : 'none'
            });

            if (!token) {
                console.error('❌ [SmartMonitoring] Няма authentication token! Трябва да се логнеш отново.');
                setMonitoringLoading(false);
                return;
            }

            // Зареди данни от всички монитори
            const [zonesRes, panelsRes, doorsRes, devicesRes] = await Promise.all([
                apiService.sprinklerMonitor.getZones().catch((err) => {
                    console.error('❌ [SmartMonitoring] Грешка при зареждане на спринклерни зони:', err);
                    console.error('❌ [SmartMonitoring] Error details:', {
                        status: err.response?.status,
                        statusText: err.response?.statusText,
                        data: err.response?.data,
                        message: err.message
                    });
                    if (err.response?.status === 401) {
                        console.error('❌ [SmartMonitoring] 401 Unauthorized - невалиден token!');
                    }
                    return { data: null, error: err };
                }),
                apiService.electricalPanelMonitor.getPanels().catch((err) => {
                    console.error('❌ [SmartMonitoring] Грешка при зареждане на ел. табла:', err);
                    if (err.response?.status === 401) {
                        console.error('❌ [SmartMonitoring] 401 Unauthorized - невалиден token!');
                    }
                    return { data: null, error: err };
                }),
                apiService.evacDoorMonitor.getDoors().catch((err) => {
                    console.error('❌ [SmartMonitoring] Грешка при зареждане на евакуационни врати:', err);
                    if (err.response?.status === 401) {
                        console.error('❌ [SmartMonitoring] 401 Unauthorized - невалиден token!');
                    }
                    return { data: null, error: err };
                }),
                apiService.devices.getAll().catch((err) => {
                    console.error('❌ [SmartMonitoring] Грешка при зареждане на устройства:', err);
                    if (err.response?.status === 401) {
                        console.error('❌ [SmartMonitoring] 401 Unauthorized - невалиден token!');
                    }
                    return { data: null, error: err };
                })
            ]);

            console.log('📦 [SmartMonitoring] Получени отговори:', {
                zones: {
                    hasData: !!zonesRes?.data,
                    structure: zonesRes?.data ? Object.keys(zonesRes.data) : [],
                    zonesCount: zonesRes?.data?.zones?.length || (Array.isArray(zonesRes?.data) ? zonesRes.data.length : 0),
                    fullData: zonesRes?.data,
                    status: zonesRes?.status,
                    error: zonesRes?.error || zonesRes?.message
                },
                panels: {
                    hasData: !!panelsRes?.data,
                    structure: panelsRes?.data ? Object.keys(panelsRes.data) : [],
                    panelsCount: panelsRes?.data?.panels?.length || (Array.isArray(panelsRes?.data) ? panelsRes.data.length : 0),
                    fullData: panelsRes?.data,
                    status: panelsRes?.status,
                    error: panelsRes?.error || panelsRes?.message
                },
                doors: {
                    hasData: !!doorsRes?.data,
                    structure: doorsRes?.data ? Object.keys(doorsRes.data) : [],
                    doorsCount: doorsRes?.data?.doors?.length || (Array.isArray(doorsRes?.data) ? doorsRes.data.length : 0),
                    fullData: doorsRes?.data,
                    status: doorsRes?.status,
                    error: doorsRes?.error || doorsRes?.message
                },
                devices: {
                    hasData: !!devicesRes?.data,
                    structure: devicesRes?.data ? Object.keys(devicesRes.data) : [],
                    devicesCount: devicesRes?.data?.devices?.length || (Array.isArray(devicesRes?.data) ? devicesRes.data.length : 0),
                    status: devicesRes?.status,
                    error: devicesRes?.error || devicesRes?.message
                }
            });

            // Обработка на устройства - провери различни структури на response
            let devices = [];
            if (devicesRes?.error) {
                console.warn('⚠️ [SmartMonitoring] Грешка при зареждане на устройства:', devicesRes.error);
                devices = [];
            } else if (devicesRes?.data) {
                console.log('🔍 [SmartMonitoring] Обработка на устройства. Структура:', {
                    isArray: Array.isArray(devicesRes.data),
                    hasDevices: !!devicesRes.data.devices,
                    hasData: !!devicesRes.data.data,
                    keys: Object.keys(devicesRes.data)
                });
                if (Array.isArray(devicesRes.data)) {
                    devices = devicesRes.data;
                } else if (devicesRes.data.devices && Array.isArray(devicesRes.data.devices)) {
                    devices = devicesRes.data.devices;
                } else if (devicesRes.data.data && Array.isArray(devicesRes.data.data)) {
                    devices = devicesRes.data.data;
                }
            } else {
                console.warn('⚠️ [SmartMonitoring] devicesRes.data е undefined или null');
            }

            // Обработка на спринклерни зони
            // Backend връща: { success: true, zones: [...] }
            let zones = [];
            console.log('🔍 [SmartMonitoring] RAW zonesRes:', zonesRes);
            
            if (zonesRes?.error) {
                console.warn('⚠️ [SmartMonitoring] Грешка при зареждане на спринклерни зони:', zonesRes.error);
                zones = [];
            } else if (zonesRes?.data) {
                // Провери дали response.data съдържа error (401, 403, etc.)
                if (zonesRes.data.error) {
                    console.error('❌ [SmartMonitoring] Backend error за zones:', zonesRes.data.error, zonesRes.data.message);
                    zones = [];
                } else if (zonesRes.data.zones && Array.isArray(zonesRes.data.zones)) {
                    zones = zonesRes.data.zones;
                    console.log('✅ [SmartMonitoring] Зонитe са в data.zones:', zones.length);
                } else if (Array.isArray(zonesRes.data)) {
                    zones = zonesRes.data;
                    console.log('✅ [SmartMonitoring] data е директно array:', zones.length);
                } else {
                    console.error('❌ [SmartMonitoring] Неразпознат формат на zones response!');
                    console.error('zonesRes.data:', zonesRes.data);
                }
            } else {
                console.warn('⚠️ [SmartMonitoring] zonesRes.data е undefined или null');
            }

            // Обработка на електрически табла
            // Backend връща: { success: true, panels: [...] }
            let panels = [];
            console.log('🔍 [SmartMonitoring] RAW panelsRes:', panelsRes);
            
            if (panelsRes?.error) {
                console.warn('⚠️ [SmartMonitoring] Грешка при зареждане на ел. табла:', panelsRes.error);
                panels = [];
            } else if (panelsRes?.data) {
                // Провери дали response.data съдържа error (401, 403, etc.)
                if (panelsRes.data.error) {
                    console.error('❌ [SmartMonitoring] Backend error за panels:', panelsRes.data.error, panelsRes.data.message);
                    panels = [];
                } else if (panelsRes.data.panels && Array.isArray(panelsRes.data.panels)) {
                    panels = panelsRes.data.panels;
                    console.log('✅ [SmartMonitoring] Таблата са в data.panels:', panels.length);
                } else if (Array.isArray(panelsRes.data)) {
                    panels = panelsRes.data;
                    console.log('✅ [SmartMonitoring] data е директно array:', panels.length);
                } else {
                    console.error('❌ [SmartMonitoring] Неразпознат формат на panels response!');
                    console.error('panelsRes.data:', panelsRes.data);
                }
            } else {
                console.warn('⚠️ [SmartMonitoring] panelsRes.data е undefined или null');
            }

            // Обработка на евакуационни врати
            // Backend връща: { success: true, doors: [...] }
            let doors = [];
            console.log('🔍 [SmartMonitoring] RAW doorsRes:', doorsRes);
            
            if (doorsRes?.error) {
                console.warn('⚠️ [SmartMonitoring] Грешка при зареждане на евакуационни врати:', doorsRes.error);
                doors = [];
            } else if (doorsRes?.data) {
                // Провери дали response.data съдържа error (401, 403, etc.)
                if (doorsRes.data.error) {
                    console.error('❌ [SmartMonitoring] Backend error за doors:', doorsRes.data.error, doorsRes.data.message);
                    doors = [];
                } else if (doorsRes.data.doors && Array.isArray(doorsRes.data.doors)) {
                    doors = doorsRes.data.doors;
                    console.log('✅ [SmartMonitoring] Вратите са в data.doors:', doors.length);
                } else if (Array.isArray(doorsRes.data)) {
                    doors = doorsRes.data;
                    console.log('✅ [SmartMonitoring] data е директно array:', doors.length);
                } else {
                    console.error('❌ [SmartMonitoring] Неразпознат формат на doors response!');
                    console.error('doorsRes.data:', doorsRes.data);
                }
            } else {
                console.warn('⚠️ [SmartMonitoring] doorsRes.data е undefined или null');
            }

            const monitoringData = {
                sprinklerZones: zones,
                electricalPanels: panels,
                evacDoors: doors,
                smokeDetectors: devices.filter(d => (d.type === 'smoke_detector' || d.device_type === 'smoke_detector')),
                heatSensors: devices.filter(d => (d.type === 'heat_sensor' || d.device_type === 'heat_sensor')),
                cameras: devices.filter(d => (d.type === 'camera' || d.device_type === 'camera')),
                alarms: devices.filter(d => (d.type === 'alarm' || d.device_type === 'alarm')),
                doorSensors: devices.filter(d => (d.type === 'door_sensor' || d.device_type === 'door_sensor'))
            };

            console.log('✅ [SmartMonitoring] Обработени данни:', {
                sprinklerZones: monitoringData.sprinklerZones.length,
                electricalPanels: monitoringData.electricalPanels.length,
                evacDoors: monitoringData.evacDoors.length,
                smokeDetectors: monitoringData.smokeDetectors.length,
                heatSensors: monitoringData.heatSensors.length,
                cameras: monitoringData.cameras.length,
                alarms: monitoringData.alarms.length,
                doorSensors: monitoringData.doorSensors.length
            });
            
            setMonitoringData(monitoringData);
        } catch (error) {
            console.error('❌ [SmartMonitoring] Критична грешка при зареждане на мониторинг данни:', error);
        } finally {
            setMonitoringLoading(false);
        }
    };

    const setupConnections = () => {
        // Setup Backend WebSocket for real-time data
        if (!DISABLE_BACKEND_WS) {
            setupBackendConnection();
        }
        
        // Setup AI Service WebSocket for AI insights
        if (!DISABLE_AI_WS) {
            setupAIConnection();
        }

        // Setup Socket.IO connection for monitor updates
        setupMonitorSocketConnection();
    };

    const setupMonitorSocketConnection = () => {
        try {
            // Вземи token от localStorage
            const token = localStorage.getItem('fireguard_token');
            if (!token) {
                console.warn('⚠️ Monitor Socket: Няма authentication token. Socket.IO връзката може да не работи.');
            }

            backendSocketRef.current = io(API_ORIGIN, {
                transports: ['websocket'],
                autoConnect: true,
                auth: {
                    token: token
                },
                query: {
                    token: token
                }
            });

            const socket = backendSocketRef.current;

            socket.on('connect', () => {
                console.log('✅ Свързан с backend Socket.IO за монитори');
                socket.emit('join', 'monitoring');
            });

            // Слушай за обновления на спринклерни зони
            socket.on('sprinkler-zone:updated', (data) => {
                console.log('💧 Sprinkler zone update:', data);
                setMonitoringData(prev => ({
                    ...prev,
                    sprinklerZones: prev.sprinklerZones.map(z => 
                        z.id === data.zone?.id ? { ...z, ...data.zone } : z
                    )
                }));
            });

            // Слушай за обновления на електрически табла
            socket.on('electrical-panel:updated', (data) => {
                console.log('⚡ Electrical panel update:', data);
                setMonitoringData(prev => ({
                    ...prev,
                    electricalPanels: prev.electricalPanels.map(p => 
                        p.id === data.panel?.id ? { ...p, ...data.panel } : p
                    )
                }));
            });

            // Слушай за обновления на евакуационни врати
            socket.on('evac-door:updated', (data) => {
                console.log('🚪 Evac door update:', data);
                setMonitoringData(prev => ({
                    ...prev,
                    evacDoors: prev.evacDoors.map(d => 
                        d.id === data.door?.id ? { ...d, ...data.door } : d
                    )
                }));
            });

            // Слушай за общи промени в състоянието на монитори
            socket.on('monitor:state-changed', (data) => {
                console.log('📊 Monitor state changed:', data);
                // Презареди данните при промяна в състоянието
                loadMonitoringData();
            });

        } catch (error) {
            console.error('Error connecting to Socket.IO:', error);
        }
    };

    const setupBackendConnection = () => {
        const handleRealtimeData = (data) => {
            console.log('📊 Raw sensor data:', data);
            updateRealtimeData(data);
        };

        const handleAlert = (alertData) => {
            console.log('⚠️ System alert:', alertData);
            // Add to regular alerts (not AI alerts)
        };

        websocketService.on('realtime_data', handleRealtimeData);
        websocketService.on('alert', handleAlert);
        websocketService.on('device_status', (data) => {
            console.log('📱 Device status update:', data);
        });
    };

    const setupAIConnection = () => {
        // Провери дали вече има активна връзка
        if (aiSocketRef.current && aiSocketRef.current.connected) {
            console.log('🤖 AI Service вече е свързан');
            return;
        }

        // Провери дали не сме надвишили максималния брой опити
        if (aiConnectionAttemptsRef.current >= MAX_AI_CONNECTION_ATTEMPTS) {
            console.warn(`⚠️ AI Service: Достигнат максимален брой опити (${MAX_AI_CONNECTION_ATTEMPTS}). Спиране на опитите.`);
            setAiStatus(prev => ({ ...prev, connected: false }));
            return;
        }

        aiConnectionAttemptsRef.current += 1;
        console.log(`🔄 AI Service: Опит за свързване ${aiConnectionAttemptsRef.current}/${MAX_AI_CONNECTION_ATTEMPTS}...`);

        // Вземи token от localStorage
        const token = localStorage.getItem('fireguard_token');
        if (!token) {
            console.warn('⚠️ AI Service: Няма authentication token. Socket.IO връзката може да не работи.');
        }

        try {
            aiSocketRef.current = io(AI_SERVICE_BASE_URL, {
                transports: ['websocket'],
                autoConnect: true,
                reconnection: true,
                reconnectionDelay: 2000,
                reconnectionDelayMax: 10000,
                reconnectionAttempts: 3,
                timeout: 5000,
                auth: {
                    token: token
                },
                query: {
                    token: token
                }
            });

            const aiSocket = aiSocketRef.current;

            aiSocket.on('connect', () => {
                console.log('✅ AI Service: Успешно свързване');
                aiConnectionAttemptsRef.current = 0; // Нулирай брояча при успешно свързване
                setAiStatus(prev => ({ ...prev, connected: true }));
                aiSocket.emit('subscribe_insights', ['device_monitoring', 'predictive_maintenance', 'emergency_response']);
                
                // Check AI status immediately after connecting (само веднъж)
                if (!aiStatusCheckRef.current) {
                    checkAIStatusViaHTTP();
                }
            });

            aiSocket.on('connect_error', (error) => {
                console.error(`❌ AI Service: Грешка при свързване (опит ${aiConnectionAttemptsRef.current}/${MAX_AI_CONNECTION_ATTEMPTS}):`, error.message || error);
                
                // Провери статуса чрез HTTP само ако не сме надвишили опитите
                if (aiConnectionAttemptsRef.current < MAX_AI_CONNECTION_ATTEMPTS) {
                    // Изчакай преди следващия опит
                    setTimeout(() => {
                        if (!aiStatusCheckRef.current) {
                            checkAIStatusViaHTTP();
                        }
                    }, 2000);
                } else {
                    console.warn('⚠️ AI Service: Спиране на опитите за свързване');
                    setAiStatus(prev => ({ ...prev, connected: false }));
                }
            });

        aiSocket.on('ai_status', (status) => {
            console.log('📊 AI Status received:', status);
            setAiStatus(prev => ({
                ...prev,
                connected: true,
                provider: status.providers?.current || status.currentProvider || 'none',
                monitoring: status.monitoring?.monitoring || false
            }));
        });

        aiSocket.on('real_time_analysis', (analysisData) => {
            console.log('🧠 AI Analysis:', analysisData);
            setAiInsights(prev => [analysisData, ...prev].slice(0, 20));
        });

        aiSocket.on('emergency_alert', (emergencyData) => {
            console.log('🚨 AI Emergency Alert:', emergencyData);
            setEmergencyAlerts(prev => [emergencyData, ...prev]);
            
            // Browser notification for critical AI alerts
            if (Notification.permission === 'granted') {
                new Notification('🚨 AI CRITICAL ALERT!', {
                    body: emergencyData.analysis?.analysis?.summary || 'Critical situation',
                    icon: '/alert-icon.png',
                    requireInteraction: true
                });
            }
        });

        aiSocket.on('maintenance_insight', (maintenanceData) => {
            setPredictions(maintenanceData);
        });
        
        aiSocket.on('disconnect', () => {
            console.log('❌ Disconnected from AI Service');
            setAiStatus(prev => ({ ...prev, connected: false }));
        });

        } catch (error) {
            console.error('❌ AI Service: Грешка при инициализация на Socket.IO:', error);
            setAiStatus(prev => ({ ...prev, connected: false }));
        }
    };
    
    // Check AI status via HTTP API
    const checkAIStatusViaHTTP = async () => {
        // Предотврати множествени едновременни проверки
        if (aiStatusCheckRef.current) {
            console.log('⏳ AI Status: Проверка вече е в процес, изчакване...');
            return;
        }

        aiStatusCheckRef.current = true;

        try {
            console.log('🔍 AI Status: Проверка чрез HTTP...');
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунди timeout

            const response = await fetch(`${AI_SERVICE_BASE_URL}/api/status`, {
                signal: controller.signal,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const status = await response.json();
                console.log('✅ AI Status: Успешна проверка:', status);
                
                // Провери дали има налични провайдъри
                const availableProviders = status.health?.available || status.available || [];
                const hasProvider = availableProviders.length > 0;
                const currentProvider = status.currentProvider || status.health?.current || 'none';
                
                // Провери статус на мониторинга
                const monitoringStatus = await fetch(`${AI_SERVICE_BASE_URL}/api/monitoring/status`)
                    .then(res => res.ok ? res.json() : { monitoring: false })
                    .catch(() => ({ monitoring: false }));
                
                console.log('📊 Setting AI status:', {
                    connected: true,
                    hasProvider: hasProvider,
                    currentProvider: currentProvider,
                    availableCount: availableProviders.length,
                    monitoring: monitoringStatus.monitoring || false
                });
                
                setAiStatus({
                    connected: true, // Услугата работи
                    provider: currentProvider,
                    monitoring: monitoringStatus.monitoring || false,
                    hasProvider: hasProvider // Има ли конфигуриран провайдер
                });
            } else {
                console.warn(`⚠️ AI Status: HTTP заявката неуспешна (${response.status})`);
                setAiStatus(prev => ({
                    ...prev,
                    connected: false
                }));
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.warn('⏱️ AI Status: Заявката отне твърде много време (timeout)');
            } else if (error.code === 'ECONNREFUSED' || error.message?.includes('Failed to fetch')) {
                console.warn('⚠️ AI Status: AI Service не е достъпен на', AI_SERVICE_BASE_URL);
            } else {
                console.error('❌ AI Status: Грешка при проверка:', error.message || error);
            }
            setAiStatus(prev => ({
                ...prev,
                connected: false
            }));
        } finally {
            // Освободи флага след 2 секунди (за да може да се направи нова проверка)
            setTimeout(() => {
                aiStatusCheckRef.current = false;
            }, 2000);
        }
    };

    const cleanup = () => {
        // НЕ спираме реално-време мониторинга при навигация – спира само при „Стоп“
        if (!DISABLE_BACKEND_WS) {
            websocketService.off('realtime_data');
            websocketService.off('alert');
            websocketService.off('device_status');
        }
        
        if (!DISABLE_AI_WS && aiSocketRef.current) {
            console.log('🔌 AI Service: Изключване на връзката');
            aiSocketRef.current.disconnect();
            aiSocketRef.current = null;
            aiConnectionAttemptsRef.current = 0; // Нулирай брояча при cleanup
        }
        aiStatusCheckRef.current = false; // Освободи флага

        // Изключи Socket.IO връзката за монитори
        if (backendSocketRef.current) {
            backendSocketRef.current.disconnect();
        }
    };

    const toggleMonitoring = () => {
        if (monitoringActive) {
            stopRealtimeFromContext();
        } else {
            startRealtimeFromContext();
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.8)'
                }
            },
            x: {
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.8)'
                }
            }
        },
        plugins: {
            legend: {
                labels: {
                    color: 'rgba(255, 255, 255, 0.9)'
                }
            }
        }
    };

    const safeLabels = Array.isArray(timeLabels) ? timeLabels : [];
    const safeTempData = Array.isArray(tempData) ? tempData : [];
    const safeSmokeData = Array.isArray(smokeData) ? smokeData : [];

    const temperatureChartData = {
        labels: safeLabels,
        datasets: [
            {
                label: 'Temperature (°C)',
                data: safeTempData,
                borderColor: '#e74c3c',
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                tension: 0.4,
                fill: true,
            }
        ]
    };

    const smokeChartData = {
        labels: safeLabels,
        datasets: [
            {
                label: 'Smoke particles (%)',
                data: safeSmokeData,
                borderColor: '#f39c12',
                backgroundColor: 'rgba(243, 156, 18, 0.1)',
                tension: 0.4,
                fill: true,
            }
        ]
    };

    const formatRiskLevel = (level) => {
        const riskMap = {
            'critical': { text: 'CRITICAL', color: '#F44336', icon: '🔴' },
            'high': { text: 'HIGH', color: '#FF9800', icon: '🟠' },
            'medium': { text: 'MEDIUM', color: '#FFC107', icon: '🟡' },
            'low': { text: 'LOW', color: '#4CAF50', icon: '🟢' },
            'висок': { text: 'CRITICAL', color: '#F44336', icon: '🔴' },
            'среден': { text: 'MEDIUM', color: '#FFC107', icon: '🟡' },
            'нисък': { text: 'LOW', color: '#4CAF50', icon: '🟢' }
        };
        return riskMap[level?.toLowerCase()] || { text: 'UNKNOWN', color: '#9E9E9E', icon: '⚪' };
    };

    const getHealthStatus = (healthLevel) => {
        const healthMap = {
            'good': { text: 'EXCELLENT', color: '#4CAF50', icon: '💚' },
            'moderate': { text: 'GOOD', color: '#8BC34A', icon: '💛' },
            'warning': { text: 'WARNING', color: '#FF9800', icon: '🧡' },
            'critical': { text: 'CRITICAL', color: '#F44336', icon: '❤️' }
        };
        return healthMap[healthLevel] || { text: 'UNKNOWN', color: '#9E9E9E', icon: '🤍' };
    };

    const getDeviceTypeIcon = (type) => {
        const icons = {
            'smoke_detector': '💨',
            'heat_sensor': '🌡️', 
            'camera': '📷',
            'sprinkler': '💧',
            'alarm': '🔔',
            'door_sensor': '🚪',
            'motion_sensor': '👋'
        };
        return icons[type] || '📱';
    };

    return (
        <div className="smart-monitoring">
            <div className="monitoring-header">
                <h2>📊 Smart monitoring</h2>
                <div className="tab-controls">
                    <button 
                        type="button"
                        className={`tab-btn ${activeTab === 'realtime' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('🟢 Clicked on "Real-time data"');
                            setActiveTab('realtime');
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        📈 Real-time data
                    </button>
                    <button 
                        type="button"
                        className={`tab-btn ${activeTab === 'ai' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('🟢 Кликнато на "AI Анализи"');
                            setActiveTab('ai');
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        🤖 AI Analysis
                    </button>
                </div>
                
                <div className="status-indicators">
                    <div className={`status-indicator ${monitoringActive ? 'active' : 'inactive'}`}>
                        {monitoringActive ? '🟢 Monitoring active' : '🔴 Monitoring stopped'}
                    </div>
                    <div className={`status-indicator ${aiStatus.connected && aiStatus.hasProvider ? 'active' : aiStatus.connected ? 'inactive' : 'inactive'}`}>
                        {aiStatus.connected && aiStatus.hasProvider
                            ? `🤖 AI active (${aiStatus.provider?.toUpperCase() || 'NONE'})`
                            : aiStatus.connected
                            ? '🤖 AI optional (add key in Settings)'
                            : '🤖 AI optional'}
                    </div>
                </div>
            </div>

            {activeTab === 'realtime' && (
                <div className="realtime-content">
                    <div className="controls-section">
                        <button 
                            className={`control-btn ${monitoringActive ? 'stop' : 'start'}`}
                            onClick={toggleMonitoring}
                        >
                            {monitoringActive ? '⏹️ Stop monitoring' : '▶️ Start monitoring'}
                        </button>
                        
                        {(monitoringActive || currentData) && (
                            <div className="current-values">
                                <div className="value-card temperature">
                                    <span className="value-icon">🌡️</span>
                                    <span className="value-number">{currentData ? (currentData.temperature?.toFixed(1) ?? '—') : '—'}</span>
                                    <span className="value-unit">°C</span>
                                </div>
                                <div className="value-card smoke">
                                    <span className="value-icon">💨</span>
                                    <span className="value-number">{currentData && currentData.smoke_level != null ? (currentData.smoke_level * 100).toFixed(1) : '—'}</span>
                                    <span className="value-unit">%</span>
                                </div>
                                <div className="value-card battery">
                                    <span className="value-icon">🔋</span>
                                    <span className="value-number">{currentData ? (currentData.battery ?? '—') : '—'}</span>
                                    <span className="value-unit">%</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="charts-grid">
                        <div className="chart-container">
                            <h3>🌡️ Temperature</h3>
                            <div className="chart-wrapper" style={{ minHeight: 280 }}>
                                {activeTab === 'realtime' && (
                                    <Line
                                        key={`temp-${chartMountIdRef.current}-${safeLabels.length}`}
                                        data={temperatureChartData}
                                        options={chartOptions}
                                    />
                                )}
                            </div>
                        </div>
                        
                        <div className="chart-container">
                            <h3>💨 Smoke particles</h3>
                            <div className="chart-wrapper" style={{ minHeight: 280 }}>
                                {activeTab === 'realtime' && (
                                    <Line
                                        key={`smoke-${chartMountIdRef.current}-${safeLabels.length}`}
                                        data={smokeChartData}
                                        options={chartOptions}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Секция за здравето на устройствата */}
                    <div className="device-health-section">
                        <div className="health-header">
                            <h3>💓 Fire equipment health</h3>
                            <button 
                                className="btn btn-secondary"
                                onClick={loadDeviceHealth}
                            >
                                🔄 Refresh
                            </button>
                        </div>

                        <div className="system-health-overview">
                            <div className="health-summary">
                                <div className="health-indicator">
                                    <span className="health-icon">
                                        {getHealthStatus(systemHealth.overall).icon}
                                    </span>
                                    <div className="health-info">
                                        <span className="health-label">Overall system status</span>
                                        <span 
                                            className="health-value"
                                            style={{ color: getHealthStatus(systemHealth.overall).color }}
                                        >
                                            {getHealthStatus(systemHealth.overall).text}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="device-counts">
                                    <div className="count-item online">
                                        <span className="count-icon">✅</span>
                                        <span className="count-label">Online</span>
                                        <span className="count-value">{systemHealth.deviceCount.online}</span>
                                    </div>
                                    <div className="count-item offline">
                                        <span className="count-icon">⚫</span>
                                        <span className="count-label">Offline</span>
                                        <span className="count-value">{systemHealth.deviceCount.offline}</span>
                                    </div>
                                    <div className="count-item alarm">
                                        <span className="count-icon">🚨</span>
                                        <span className="count-label">Alarm</span>
                                        <span className="count-value">{systemHealth.deviceCount.alarm}</span>
                                    </div>
                                    <div className="count-item total">
                                        <span className="count-icon">📊</span>
                                        <span className="count-label">Total</span>
                                        <span className="count-value">{systemHealth.deviceCount.total}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {deviceHealth.length > 0 && (
                            <div className="device-list">
                                <h4>📱 Device status</h4>
                                <div className="devices-grid">
                                    {deviceHealth.slice(0, 8).map((device, index) => (
                                        <div key={device.id || index} className={`device-card ${device.status}`}>
                                            <div className="device-header">
                                                <span className="device-icon">
                                                    {getDeviceTypeIcon(device.device_type)}
                                                </span>
                                                <span className="device-name">{device.device_name}</span>
                                            </div>
                                            
                                            <div className="device-status">
                                                <span className={`status-badge ${device.status}`}>
                                                    {device.status === 'online' && '✅ Online'}
                                                    {device.status === 'offline' && '⚫ Offline'}
                                                    {device.status === 'alarm' && '🚨 Alarm'}
                                                </span>
                                            </div>

                                            <div className="device-metrics">
                                                <div className="metric">
                                                    <span className="metric-label">📍 Location:</span>
                                                    <span className="metric-value">{device.location || 'N/A'}</span>
                                                </div>
                                                
                                                {device.battery_percentage && (
                                                    <div className="metric">
                                                        <span className="metric-label">🔋 Battery:</span>
                                                        <span className={`metric-value ${device.battery_percentage < 20 ? 'low-battery' : ''}`}>
                                                            {device.battery_percentage}%
                                                        </span>
                                                    </div>
                                                )}

                                                {device.current_temperature && (
                                                    <div className="metric">
                                                        <span className="metric-label">🌡️ Temperature:</span>
                                                        <span className="metric-value">
                                                            {device.current_temperature.toFixed(1)}°C
                                                        </span>
                                                    </div>
                                                )}

                                                {device.last_maintenance && (
                                                    <div className="metric">
                                                        <span className="metric-label">🔧 Maintenance:</span>
                                                        <span className="metric-value">
                                                            {new Date(device.last_maintenance).toLocaleDateString('bg-BG')}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {deviceHealth.length > 8 && (
                                    <div className="show-more">
                                        <span>And {deviceHealth.length - 8} more devices...</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {systemHealth.lastCheck && (
                            <div className="last-check">
                                <span>📅 Last check: {new Date(systemHealth.lastCheck).toLocaleString('en-GB')}</span>
                            </div>
                        )}
                    </div>

                    {/* Fire Safety Equipment Monitoring Sections */}
                    <div className="fire-safety-monitoring">
                        <div className="chart-container monitoring-header-container">
                            <h3>🔥 Fire equipment monitoring</h3>
                            <div className="monitoring-header-actions">
                                <button 
                                    onClick={loadMonitoringData}
                                    disabled={monitoringLoading}
                                    style={{
                                        padding: '8px 16px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        borderRadius: '8px',
                                        color: 'white',
                                        cursor: monitoringLoading ? 'not-allowed' : 'pointer',
                                        fontSize: '0.9em',
                                        opacity: monitoringLoading ? 0.5 : 1,
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    {monitoringLoading ? '⏳ Loading...' : '🔄 Refresh'}
                                </button>
                            </div>
                        </div>

                        {monitoringLoading && (
                            <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.7)' }}>
                                <div className="spinner" style={{ 
                                    border: '4px solid rgba(255, 255, 255, 0.3)', 
                                    borderTop: '4px solid white', 
                                    borderRadius: '50%', 
                                    width: '50px', 
                                    height: '50px', 
                                    animation: 'spin 1s linear infinite',
                                    margin: '0 auto 20px'
                                }}></div>
                                Loading data...
                            </div>
                        )}

                        {/* Спринклерни зони */}
                        <div className="monitoring-section">
                            <h4>💧 Sprinkler zones ({monitoringData.sprinklerZones.length})</h4>
                            {monitoringData.sprinklerZones.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.sprinklerZones.map((zone) => (
                                        <div key={zone.id} className={`monitoring-card monitoring-${zone.state?.toLowerCase() || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">💧</span>
                                                <span className="monitoring-name">{zone.name}</span>
                                                <span className={`monitoring-state state-${zone.state?.toLowerCase() || 'normal'}`}>
                                                    {zone.state || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {zone.location}</div>
                                                {zone.pressure_main !== null && (
                                                    <div className="monitoring-metric">
                                                        Pressure: <strong>{zone.pressure_main?.toFixed(1)} bar</strong>
                                                    </div>
                                                )}
                                                {zone.last_alarm_at && (
                                                    <div className="monitoring-timestamp">
                                                        Last ALARM: {new Date(zone.last_alarm_at).toLocaleString('en-GB')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No sprinkler zones registered
                                </div>
                            )}
                        </div>

                        {/* Електрически табла */}
                        <div className="monitoring-section">
                            <h4>⚡ Electrical panels ({monitoringData.electricalPanels.length})</h4>
                            {monitoringData.electricalPanels.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.electricalPanels.map((panel) => (
                                        <div key={panel.id} className={`monitoring-card monitoring-${panel.state?.toLowerCase() || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">⚡</span>
                                                <span className="monitoring-name">{panel.name}</span>
                                                <span className={`monitoring-state state-${panel.state?.toLowerCase() || 'normal'}`}>
                                                    {panel.state || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {panel.location}</div>
                                                {panel.temp_panel !== null && (
                                                    <div className="monitoring-metric">
                                                        Temperature: <strong>{panel.temp_panel?.toFixed(1)}°C</strong>
                                                    </div>
                                                )}
                                                {panel.current_phase_L1 !== null && (
                                                    <div className="monitoring-metric">
                                                        Current L1: <strong>{panel.current_phase_L1?.toFixed(1)}A</strong>
                                                    </div>
                                                )}
                                                {panel.last_alarm_at && (
                                                    <div className="monitoring-timestamp">
                                                        Last ALARM: {new Date(panel.last_alarm_at).toLocaleString('en-GB')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No electrical panels registered
                                </div>
                            )}
                        </div>

                        {/* Евакуационни врати */}
                        <div className="monitoring-section">
                            <h4>🚪 Evacuation doors ({monitoringData.evacDoors.length})</h4>
                            {monitoringData.evacDoors.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.evacDoors.map((door) => (
                                        <div key={door.id} className={`monitoring-card monitoring-${door.state?.toLowerCase() || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">🚪</span>
                                                <span className="monitoring-name">{door.name}</span>
                                                <span className={`monitoring-state state-${door.state?.toLowerCase() || 'normal'}`}>
                                                    {door.state || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {door.location}</div>
                                                <div className="monitoring-metric">
                                                    Type: <strong>{door.door_type === 'evacuation' ? 'Evacuation' : 'Fire-resistant'}</strong>
                                                </div>
                                                <div className="monitoring-status">
                                                    {door.door_closed ? '🔒 Closed' : '🔓 Open'} | 
                                                    {door.panic_bar_ok ? ' ✅ Panic bar OK' : ' ⚠️ Panic bar issue'}
                                                </div>
                                                {door.last_alarm_at && (
                                                    <div className="monitoring-timestamp">
                                                        Last ALARM: {new Date(door.last_alarm_at).toLocaleString('en-GB')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No evacuation doors registered
                                </div>
                            )}
                        </div>

                        {/* Детектори за дим */}
                        <div className="monitoring-section">
                            <h4>💨 Smoke detectors ({monitoringData.smokeDetectors.length})</h4>
                            {monitoringData.smokeDetectors.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.smokeDetectors.map((detector) => (
                                        <div key={detector.id} className={`monitoring-card monitoring-${detector.status || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">💨</span>
                                                <span className="monitoring-name">{detector.name || detector.device_name}</span>
                                                <span className={`monitoring-state state-${detector.status || 'normal'}`}>
                                                    {detector.status || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {detector.location || 'N/A'}</div>
                                                {detector.current_temperature && (
                                                    <div className="monitoring-metric">
                                                        Temperature: <strong>{detector.current_temperature?.toFixed(1)}°C</strong>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No smoke detectors registered
                                </div>
                            )}
                        </div>

                        {/* Температурни сензори */}
                        <div className="monitoring-section">
                            <h4>🌡️ Heat sensors ({monitoringData.heatSensors.length})</h4>
                            {monitoringData.heatSensors.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.heatSensors.map((sensor) => (
                                        <div key={sensor.id} className={`monitoring-card monitoring-${sensor.status || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">🌡️</span>
                                                <span className="monitoring-name">{sensor.name || sensor.device_name}</span>
                                                <span className={`monitoring-state state-${sensor.status || 'normal'}`}>
                                                    {sensor.status || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {sensor.location || 'N/A'}</div>
                                                {sensor.current_temperature && (
                                                    <div className="monitoring-metric">
                                                        Temperature: <strong>{sensor.current_temperature?.toFixed(1)}°C</strong>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No heat sensors registered
                                </div>
                            )}
                        </div>

                        {/* Камери */}
                        <div className="monitoring-section">
                            <h4>📹 Cameras ({monitoringData.cameras.length})</h4>
                            {monitoringData.cameras.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.cameras.map((camera) => (
                                        <div key={camera.id} className={`monitoring-card monitoring-${camera.status || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">📹</span>
                                                <span className="monitoring-name">{camera.name || camera.device_name}</span>
                                                <span className={`monitoring-state state-${camera.status || 'normal'}`}>
                                                    {camera.status || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {camera.location || 'N/A'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No cameras registered
                                </div>
                            )}
                        </div>

                        {/* Алармени системи */}
                        <div className="monitoring-section">
                            <h4>🔔 Alarm systems ({monitoringData.alarms.length})</h4>
                            {monitoringData.alarms.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.alarms.map((alarm) => (
                                        <div key={alarm.id} className={`monitoring-card monitoring-${alarm.status || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">🔔</span>
                                                <span className="monitoring-name">{alarm.name || alarm.device_name}</span>
                                                <span className={`monitoring-state state-${alarm.status || 'normal'}`}>
                                                    {alarm.status || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {alarm.location || 'N/A'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No alarm systems registered
                                </div>
                            )}
                        </div>

                        {/* Сензори за врати */}
                        <div className="monitoring-section">
                            <h4>🚪 Door sensors ({monitoringData.doorSensors.length})</h4>
                            {monitoringData.doorSensors.length > 0 ? (
                                <div className="monitoring-grid">
                                    {monitoringData.doorSensors.map((sensor) => (
                                        <div key={sensor.id} className={`monitoring-card monitoring-${sensor.status || 'normal'}`}>
                                            <div className="monitoring-card-header">
                                                <span className="monitoring-icon">🚪</span>
                                                <span className="monitoring-name">{sensor.name || sensor.device_name}</span>
                                                <span className={`monitoring-state state-${sensor.status || 'normal'}`}>
                                                    {sensor.status || 'NORMAL'}
                                                </span>
                                            </div>
                                            <div className="monitoring-details">
                                                <div className="monitoring-location">📍 {sensor.location || 'N/A'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="monitoring-no-data">
                                    No door sensors registered
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'ai' && (
                <div className="ai-content">
                    <div className="ai-controls">
                        <div className="ai-status">
                            <span className="ai-provider">
                                🤖 {aiStatus.provider?.toUpperCase() || 'NO PROVIDER'}
                            </span>
                            <span className={`ai-connection ${aiStatus.connected ? 'connected' : 'disconnected'}`}>
                                {aiStatus.connected ? '🔗 Connected' : '❌ Disconnected'}
                            </span>
                        </div>
                    </div>

                    {emergencyAlerts.length > 0 && (
                        <div className="emergency-section">
                            <h3>🚨 AI Emergency alerts</h3>
                            <div className="emergency-alerts">
                                {emergencyAlerts.slice(0, 3).map((alert, index) => (
                                    <div key={index} className="emergency-alert">
                                        <div className="alert-header">
                                            <span className="alert-time">
                                                {new Date(alert.timestamp).toLocaleTimeString('en-GB')}
                                            </span>
                                            <span className="alert-level critical">🚨 CRITICAL</span>
                                        </div>
                                        <div className="alert-content">
                                            {alert.analysis?.analysis?.summary || 'AI detected a critical situation'}
                                        </div>
                                        {alert.analysis?.analysis?.recommendations && (
                                            <div className="alert-recommendations">
                                                <strong>AI Recommendations:</strong>
                                                <ul>
                                                    {alert.analysis.analysis.recommendations.slice(0, 2).map((rec, i) => (
                                                        <li key={i}>{rec}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="ai-insights-section">
                        <h3>🧠 AI Real-time analysis</h3>
                        <div className="insights-list">
                            {aiInsights.length === 0 ? (
                                <div className="no-insights">
                                    <p>No AI analysis to display</p>
                                    <small>AI analysis will appear when sensor data is received</small>
                                </div>
                            ) : (
                                aiInsights.map((insight, index) => {
                                    const risk = formatRiskLevel(insight.analysis?.analysis?.risk_level);
                                    return (
                                        <div key={index} className="insight-card">
                                            <div className="insight-header">
                                                <span className="insight-time">
                                                    {new Date(insight.timestamp).toLocaleTimeString('en-GB')}
                                                </span>
                                                <span className="insight-device">
                                                    📱 {insight.deviceId || 'System'}
                                                </span>
                                                <span 
                                                    className="risk-badge"
                                                    style={{ backgroundColor: risk.color }}
                                                >
                                                    {risk.icon} {risk.text}
                                                </span>
                                            </div>
                                            
                                            {insight.analysis?.analysis?.summary && (
                                                <div className="insight-summary">
                                                    {insight.analysis.analysis.summary}
                                                </div>
                                            )}
                                            
                                            {insight.analysis?.analysis?.recommendations && (
                                                <div className="insight-recommendations">
                                                    <strong>AI Recommendations:</strong>
                                                    <ul>
                                                        {insight.analysis.analysis.recommendations.slice(0, 3).map((rec, i) => (
                                                            <li key={i}>{rec}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            
                                            <div className="insight-footer">
                                                <span className="ai-provider-tag">
                                                    🤖 {insight.analysis?.provider}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {predictions && (
                        <div className="predictions-section">
                            <h3>🔮 Predictive analysis</h3>
                            <div className="prediction-content">
                                <div className="prediction-time">
                                    Last analysis: {new Date(predictions.timestamp).toLocaleString('en-GB')}
                                </div>
                                {/* Prediction details would go here */}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SmartMonitoring;
